import React, { useState, useEffect } from "react";
import "./RatingDialog.css";

interface RatingDialogProps {
  onSubmit: (rating: number, feedback?: string) => void;
  onDismiss: () => void;
  onClose: () => void;
}

const RatingDialog: React.FC<RatingDialogProps> = ({ onSubmit, onDismiss, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onDismiss]);

  function handleRate(stars: number) {
    setRating(stars);
    if (stars === 5) {
      setShowReviewPrompt(true);
      setShowFeedback(false);
    } else {
      setShowFeedback(true);
      setShowReviewPrompt(false);
    }
  }

  function handleSubmit() {
    onSubmit(rating, showFeedback ? feedback : undefined);
    setSubmitted(true);
  }

  function handleReviewAndSubmit() {
    onSubmit(5, "clicked_leave_review");
    try {
      const microsoftTeams = require("@microsoft/teams-js");
      microsoftTeams.app.openLink("https://teams.microsoft.com/l/app/72ef871e-2e9f-4a48-92bf-5bb2918a3a59");
    } catch {
      window.open("https://marketplace.microsoft.com/en-us/product/WA200011273?tab=Reviews", "_blank");
    }
    onClose();
  }

  if (submitted) {
    return (
      <div className="rating-overlay" onClick={onClose}>
        <div className="rating-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="rating-body rating-thankyou">
            <div className="rating-thankyou-emoji">🎉</div>
            <h3 className="rating-thankyou-title">Thank you!</h3>
            <p className="rating-thankyou-text">Your feedback helps us improve.</p>
          </div>
          <div className="rating-footer">
            <button className="rating-btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rating-overlay" onClick={onDismiss}>
      <div className="rating-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="rating-header">
          <h3>How are you liking Gantt Project Planner?</h3>
          <button className="rating-close" onClick={onDismiss}>&#x2715;</button>
        </div>

        <div className="rating-body">
          <div className="rating-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                className={`rating-star ${star <= (hoveredStar || rating) ? "active" : ""}`}
                onClick={() => handleRate(star)}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
              >
                ★
              </button>
            ))}
          </div>

          {showFeedback && (
            <div className="rating-feedback">
              <p>We'd love to hear how we can improve:</p>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="What could we do better?"
                rows={3}
                autoFocus
              />
            </div>
          )}

          {showReviewPrompt && (
            <div className="rating-review-prompt">
              <p>Glad you're enjoying it! Would you mind leaving a review on the Teams app store? It really helps us grow.</p>
            </div>
          )}
        </div>

        <div className="rating-footer">
          {!rating && (
            <button className="rating-btn-dismiss" onClick={onDismiss}>Not now</button>
          )}
          {showFeedback && (
            <>
              <button className="rating-btn-dismiss" onClick={onDismiss}>Not now</button>
              <button className="rating-btn-primary" onClick={handleSubmit}>Send Feedback</button>
            </>
          )}
          {showReviewPrompt && (
            <>
              <button className="rating-btn-dismiss" onClick={() => { onSubmit(5); onClose(); }}>No thanks</button>
              <button className="rating-btn-primary" onClick={handleReviewAndSubmit}>Leave a Review</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatingDialog;
